import { deviceApi } from '../api';
import { catalogNameFromPath } from './catalogPaths';
import { parseSettingsContent, SETTINGS_PATH } from './catalogSync';
import { hasDeviceId, readDevicesFromSettings } from './deviceSettings';

export type DeployValidationOptions = {
  hardwareRef?: string;
};

function catalogFolderFromPath(path: string): 'layouts' | 'signals' | 'flows' | null {
  if (path.startsWith('/layouts/')) return 'layouts';
  if (path.startsWith('/signals/')) return 'signals';
  if (path.startsWith('/flows/')) return 'flows';
  return null;
}

function catalogHasPath(settings: Record<string, unknown>, path: string): boolean {
  const folder = catalogFolderFromPath(path);
  if (!folder) return true;
  const id = catalogNameFromPath(path, folder);
  if (!id) return false;

  if (folder === 'flows') {
    return true;
  }
  if (folder === 'layouts') {
    const display = settings.display;
    const layouts = display && typeof display === 'object' && Array.isArray((display as { layouts?: unknown }).layouts)
      ? (display as { layouts: { id?: string; path?: string }[] }).layouts
      : [];
    return layouts.some((entry) => entry.path === path || entry.id === id);
  }
  if (folder === 'signals') {
    const signals = settings.signals;
    const files = signals && typeof signals === 'object' && Array.isArray((signals as { files?: unknown }).files)
      ? (signals as { files: { id?: string; path?: string }[] }).files
      : [];
    return files.some((entry) => entry.path === path || entry.id === id);
  }
  return false;
}

export async function validateDeployTarget(
  deviceId: string,
  path: string,
  options?: DeployValidationOptions,
): Promise<string[]> {
  const warnings: string[] = [];
  const stored = await deviceApi.getStoredFile(deviceId, SETTINGS_PATH);
  if (!stored?.content) {
    warnings.push('deploy.validation.noSettings');
    return warnings;
  }

  const settings = parseSettingsContent(stored.content);

  if (path.startsWith('/signals/')) {
    if (!options?.hardwareRef) {
      warnings.push('deploy.validation.missingHardwareRef');
    } else if (!hasDeviceId(stored.content, options.hardwareRef)) {
      warnings.push('deploy.validation.unknownHardwareRef');
    } else {
      const device = readDevicesFromSettings(stored.content).find((item) => item.id === options.hardwareRef);
      if (device && device.type !== 'stacklight') {
        warnings.push('deploy.validation.wrongHardwareType');
      }
    }
  }

  if (!catalogHasPath(settings, path)) {
    warnings.push('deploy.validation.missingCatalogEntry');
  }

  return warnings;
}
